/**
 * URLパラメータでフィルタ条件を管理するヘルパー関数群
 *
 * フィルタ条件をURLクエリパラメータとして保存・復元し、
 * 詳細ページから一覧ページへ戻る際にフィルタ状態を維持する。
 */

import type { FilterState } from '@/types/domain';

/**
 * フィルタ条件をURLクエリパラメータに変換
 */
export function buildFilterQueryParams(filters: Partial<FilterState>): string {
  const params = new URLSearchParams();

  // ジャンル
  if (filters.genres && filters.genres.length > 0) {
    params.set('genres', filters.genres.join(','));
  }

  // 発表形態
  if (filters.artforms && filters.artforms.length > 0) {
    params.set('artforms', filters.artforms.join(','));
  }

  // キーワード
  if (filters.keywords && filters.keywords.length > 0) {
    params.set('keywords', filters.keywords.join(','));
  }

  // グループ
  if (filters.groups && filters.groups.length > 0) {
    params.set('groups', filters.groups.join(','));
  }

  // 制作年範囲
  if (filters.dateRange && filters.dateRange.length === 2) {
    params.set('yearFrom', filters.dateRange[0].toString());
    params.set('yearTo', filters.dateRange[1].toString());
  }

  // 検索クエリ
  if (filters.searchQuery) {
    params.set('q', filters.searchQuery);
  }

  return params.toString();
}

/**
 * URLクエリパラメータからフィルタ条件を復元
 */
export function parseFilterQueryParams(
  searchParams: URLSearchParams
): Partial<FilterState> {
  const filters: Partial<FilterState> = {};

  // ジャンル
  const genres = searchParams.get('genres');
  if (genres) {
    filters.genres = genres
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
  }

  // 発表形態
  const artforms = searchParams.get('artforms');
  if (artforms) {
    filters.artforms = artforms
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);
  }

  // キーワード
  const keywords = searchParams.get('keywords');
  if (keywords) {
    filters.keywords = keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
  }

  // グループ
  const groups = searchParams.get('groups');
  if (groups) {
    filters.groups = groups
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
  }

  // 制作年範囲
  const yearFrom = searchParams.get('yearFrom');
  const yearTo = searchParams.get('yearTo');
  if (yearFrom && yearTo) {
    const isValidYear = /^\d{4}$/;
    if (isValidYear.test(yearFrom) && isValidYear.test(yearTo)) {
      filters.dateRange = [
        Number.parseInt(yearFrom, 10),
        Number.parseInt(yearTo, 10),
      ];
    }
  }

  // 検索クエリ
  const searchQuery = searchParams.get('q');
  if (searchQuery) {
    filters.searchQuery = searchQuery;
  }

  return filters;
}

/**
 * 単一のフィルタ条件（例: genre=SF）からクエリパラメータを生成
 */
export function buildSingleFilterParam(
  key: string,
  value: string
): URLSearchParams {
  const params = new URLSearchParams();
  params.set(key, value);
  return params;
}

/**
 * タイムラインフィルタ条件の型
 */
export interface TimelineFilterParams {
  /** 選択されたジャンル */
  genres?: string[];
  /** 年代範囲（10年単位の開始年） */
  decadeRange?: [number, number];
}

/**
 * タイムラインフィルタ条件をURLクエリパラメータに変換
 */
export function buildTimelineQueryParams(
  filters: TimelineFilterParams
): string {
  const params = new URLSearchParams();

  // ジャンル
  if (filters.genres && filters.genres.length > 0) {
    params.set('genres', filters.genres.join(','));
  }

  // 年代範囲
  if (filters.decadeRange && filters.decadeRange.length === 2) {
    params.set('decadeFrom', filters.decadeRange[0].toString());
    params.set('decadeTo', filters.decadeRange[1].toString());
  }

  return params.toString();
}

/**
 * URLクエリパラメータからタイムラインフィルタ条件を復元
 */
export function parseTimelineQueryParams(
  searchParams: URLSearchParams
): TimelineFilterParams {
  const filters: TimelineFilterParams = {};

  // ジャンル
  const genres = searchParams.get('genres');
  if (genres) {
    filters.genres = genres
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
  }

  // 年代範囲
  const decadeFrom = searchParams.get('decadeFrom');
  const decadeTo = searchParams.get('decadeTo');
  if (decadeFrom && decadeTo) {
    const isValidDecade = /^\d{4}$/;
    if (isValidDecade.test(decadeFrom) && isValidDecade.test(decadeTo)) {
      filters.decadeRange = [
        Number.parseInt(decadeFrom, 10),
        Number.parseInt(decadeTo, 10),
      ];
    }
  }

  return filters;
}
